import type { Area } from 'react-easy-crop'

/** Max long edge for interactive blur and crop export (px). */
export const MAX_OUTPUT_LONG_EDGE = 3840

/** Default JPEG quality (crop export, previews). */
export const EXPORT_JPEG_QUALITY = 0.96

export type CropExportBackground = 'white' | 'transparent'

function setHighQualitySmoothing(ctx: CanvasRenderingContext2D) {
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
}

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = url
  })

const normalizeRotationDeg = (deg: number) => ((deg % 360) + 360) % 360

function exportRotatedCrop(
  image: HTMLImageElement,
  pixelCrop: Area,
  rotationDeg: number,
  outW: number,
  outH: number,
  background: CropExportBackground
): HTMLCanvasElement {
  const rot = normalizeRotationDeg(rotationDeg)
  const maxSize = Math.max(image.width, image.height)
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2))

  const work = document.createElement('canvas')
  work.width = safeArea
  work.height = safeArea
  const wctx = work.getContext('2d')
  if (!wctx) {
    throw new Error('Could not get canvas context')
  }
  setHighQualitySmoothing(wctx)

  wctx.translate(safeArea / 2, safeArea / 2)
  wctx.rotate((rot * Math.PI) / 180)
  wctx.translate(-image.width / 2, -image.height / 2)
  wctx.drawImage(image, 0, 0)

  const data = wctx.getImageData(0, 0, safeArea, safeArea)

  const tmp = document.createElement('canvas')
  tmp.width = Math.max(1, Math.round(pixelCrop.width))
  tmp.height = Math.max(1, Math.round(pixelCrop.height))
  const tctx = tmp.getContext('2d')
  if (!tctx) {
    throw new Error('Could not get canvas context')
  }

  tctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  )

  const out = document.createElement('canvas')
  out.width = outW
  out.height = outH
  const octx = out.getContext('2d')
  if (!octx) {
    throw new Error('Could not get canvas context')
  }
  setHighQualitySmoothing(octx)

  if (background === 'white') {
    octx.fillStyle = '#ffffff'
    octx.fillRect(0, 0, outW, outH)
  } else {
    octx.clearRect(0, 0, outW, outH)
  }

  octx.drawImage(tmp, 0, 0, tmp.width, tmp.height, 0, 0, outW, outH)
  return out
}

/**
 * Retourne l’image en miroir horizontal (data URL PNG, sans perte).
 */
export function bakeImageHorizontalFlip(imageSrc: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const w = img.naturalWidth
      const h = img.naturalHeight
      const c = document.createElement('canvas')
      c.width = w
      c.height = h
      const ctx = c.getContext('2d')
      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }
      setHighQualitySmoothing(ctx)
      ctx.translate(w, 0)
      ctx.scale(-1, 1)
      ctx.drawImage(img, 0, 0)
      resolve(c.toDataURL('image/png'))
    }
    img.onerror = reject
    img.src = imageSrc
  })
}

/**
 * Retourne l’image en miroir vertical (data URL PNG).
 */
export function bakeImageVerticalFlip(imageSrc: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const w = img.naturalWidth
      const h = img.naturalHeight
      const c = document.createElement('canvas')
      c.width = w
      c.height = h
      const ctx = c.getContext('2d')
      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }
      setHighQualitySmoothing(ctx)
      ctx.translate(0, h)
      ctx.scale(1, -1)
      ctx.drawImage(img, 0, 0)
      resolve(c.toDataURL('image/png'))
    }
    img.onerror = reject
    img.src = imageSrc
  })
}

/**
 * Export `pixelCrop` (natural-space coordinates + react-easy-crop rotation).
 */
export async function exportCroppedImage(
  imageSrc: string,
  pixelCrop: Area | null | undefined,
  options: {
    background: CropExportBackground
    jpegQuality?: number
    /** Degrees; same convention as react-easy-crop. */
    rotation?: number
  }
): Promise<{ blob: Blob; filename: string; mimeType: string }> {
  const image = await loadImage(imageSrc)
  const rotation = options.rotation ?? 0
  const rotNorm = normalizeRotationDeg(rotation)

  const iw = image.naturalWidth || image.width
  const ih = image.naturalHeight || image.height

  const { x, y, width, height } = pixelCrop ?? {
    x: 0,
    y: 0,
    width: iw,
    height: ih,
  }
  const longEdge = Math.max(width, height)
  const scale =
    longEdge > MAX_OUTPUT_LONG_EDGE ? MAX_OUTPUT_LONG_EDGE / longEdge : 1
  const outW = Math.max(1, Math.round(width * scale))
  const outH = Math.max(1, Math.round(height * scale))

  let canvas: HTMLCanvasElement

  if (rotNorm === 0) {
    canvas = document.createElement('canvas')
    canvas.width = outW
    canvas.height = outH
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Could not get canvas context')
    }
    setHighQualitySmoothing(ctx)

    if (options.background === 'white') {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, outW, outH)
    } else {
      ctx.clearRect(0, 0, outW, outH)
    }

    const srcX1 = Math.max(0, x)
    const srcY1 = Math.max(0, y)
    const srcX2 = Math.min(iw, x + width)
    const srcY2 = Math.min(ih, y + height)

    if (srcX2 > srcX1 && srcY2 > srcY1) {
      const sw = srcX2 - srcX1
      const sh = srcY2 - srcY1
      const dx = (srcX1 - x) * scale
      const dy = (srcY1 - y) * scale
      const dw = sw * scale
      const dh = sh * scale
      ctx.drawImage(image, srcX1, srcY1, sw, sh, dx, dy, dw, dh)
    }
  } else {
    canvas = exportRotatedCrop(
      image,
      { x, y, width, height },
      rotNorm,
      outW,
      outH,
      options.background
    )
  }

  const mimeType =
    options.background === 'transparent' ? 'image/png' : 'image/jpeg'
  const quality =
    options.background === 'transparent'
      ? undefined
      : (options.jpegQuality ?? EXPORT_JPEG_QUALITY)

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) {
          resolve(b)
        } else {
          reject(new Error('toBlob failed'))
        }
      },
      mimeType,
      quality
    )
  })

  const ext = mimeType === 'image/png' ? 'png' : 'jpeg'
  return { blob, filename: `image.${ext}`, mimeType }
}
